import React, { useState, useEffect, useCallback } from 'react';
import { FileSpreadsheet, Link2, Unlink, CheckCircle, AlertCircle, RefreshCw, Plus, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleSettingsTab = () => {
  const [googleStatus, setGoogleStatus] = useState({
    connected: false,
    email: null,
    spreadsheet_configured: false,
    spreadsheet_name: null,
    spreadsheet_id: null
  });
  const [googleLoading, setGoogleLoading] = useState(false);
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [showSpreadsheetModal, setShowSpreadsheetModal] = useState(false);
  const [newSheetName, setNewSheetName] = useState('Panthers Form Submissions');
  const [syncStatus, setSyncStatus] = useState(null);

  const fetchGoogleStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/google/status`);
      const data = await response.json();
      setGoogleStatus(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Google status fetch failed:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchGoogleStatus();
    
    // Check URL params for Google OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_connected') === 'true') {
      window.history.replaceState({}, document.title, '/admin');
      setSyncStatus({ type: 'success', message: 'Google account connected successfully!' });
    }
    if (urlParams.get('google_error') === 'true') {
      window.history.replaceState({}, document.title, '/admin');
      setSyncStatus({ type: 'error', message: 'Failed to connect Google account. Please try again.' });
    }
  }, [fetchGoogleStatus]);

  const connectGoogle = () => {
    // Pass the current origin so the backend redirects back to the exact URL
    // the admin was using (preview, production, or custom domain).
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `${API_URL}/api/oauth/sheets/login?origin=${origin}`;
  };

  const disconnectGoogle = async () => {
    setGoogleLoading(true);
    try {
      await fetch(`${API_URL}/api/google/disconnect`, { method: 'POST' });
      setGoogleStatus({
        connected: false,
        email: null,
        spreadsheet_configured: false,
        spreadsheet_name: null,
        spreadsheet_id: null
      });
      setSyncStatus({ type: 'success', message: 'Google account disconnected.' });
    } catch (err) {
      setSyncStatus({ type: 'error', message: 'Failed to disconnect Google account.' });
    } finally {
      setGoogleLoading(false);
    }
  };

  const fetchSpreadsheets = async () => {
    setGoogleLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/google/spreadsheets`);
      const data = await response.json();
      setSpreadsheets(data.spreadsheets || []);
      setShowSpreadsheetModal(true);
    } catch (err) {
      setSyncStatus({ type: 'error', message: 'Failed to fetch spreadsheets.' });
    } finally {
      setGoogleLoading(false);
    }
  };

  const createNewSpreadsheet = async () => {
    setGoogleLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/google/spreadsheet/create?name=${encodeURIComponent(newSheetName)}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setSyncStatus({ type: 'success', message: `Created spreadsheet: ${data.spreadsheet_name}` });
        setShowSpreadsheetModal(false);
        fetchGoogleStatus();
      } else {
        throw new Error(data.detail || 'Failed to create spreadsheet');
      }
    } catch (err) {
      setSyncStatus({ type: 'error', message: err.message || 'Failed to create spreadsheet.' });
    } finally {
      setGoogleLoading(false);
    }
  };

  const selectSpreadsheet = async (id, name) => {
    setGoogleLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/google/spreadsheet/select?spreadsheet_id=${encodeURIComponent(id)}&spreadsheet_name=${encodeURIComponent(name)}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setSyncStatus({ type: 'success', message: `Selected spreadsheet: ${name}` });
        setShowSpreadsheetModal(false);
        fetchGoogleStatus();
      } else {
        throw new Error(data.detail || 'Failed to select spreadsheet');
      }
    } catch (err) {
      setSyncStatus({ type: 'error', message: err.message || 'Failed to select spreadsheet.' });
    } finally {
      setGoogleLoading(false);
    }
  };

  const syncExistingData = async () => {
    setGoogleLoading(true);
    setSyncStatus(null);
    try {
      const response = await fetch(`${API_URL}/api/google/sync-existing`, { method: 'POST' });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Invalid response from server');
      }
      if (data.success) {
        setSyncStatus({ type: 'success', message: data.message });
      } else {
        throw new Error(data.detail || 'Failed to sync data');
      }
    } catch (err) {
      setSyncStatus({ type: 'error', message: err.message || 'Failed to sync data to Google Sheet.' });
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="settings-section">
      <Card className="settings-card">
        <CardHeader>
          <CardTitle className="settings-title">
            <FileSpreadsheet size={24} />
            Google Sheets Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          {syncStatus && (
            <div className={`sync-status ${syncStatus.type}`}>
              {syncStatus.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              {syncStatus.message}
            </div>
          )}
          
          {!googleStatus.connected ? (
            <div className="google-connect-section">
              <p className="settings-description">
                Connect your Google account to automatically sync form submissions (OTA signups, contact messages, sponsorship inquiries) to a Google Spreadsheet.
              </p>
              <Button 
                onClick={connectGoogle} 
                className="google-connect-btn"
                disabled={googleLoading}
              >
                <Link2 size={18} />
                Connect Google Account
              </Button>
            </div>
          ) : (
            <div className="google-connected-section">
              <div className="google-account-info">
                <CheckCircle size={20} className="connected-icon" />
                <div>
                  <span className="connected-label">Connected as:</span>
                  <span className="connected-email">{googleStatus.email || 'Google Account'}</span>
                </div>
              </div>
              
              <div className="google-actions">
                {!googleStatus.spreadsheet_configured ? (
                  <div className="spreadsheet-setup">
                    <p>Select or create a spreadsheet to sync form submissions:</p>
                    <div className="spreadsheet-buttons">
                      <Button 
                        onClick={fetchSpreadsheets} 
                        className="select-sheet-btn"
                        disabled={googleLoading}
                      >
                        <FileSpreadsheet size={16} />
                        Select Existing Sheet
                      </Button>
                      <Button 
                        onClick={createNewSpreadsheet} 
                        className="create-sheet-btn"
                        disabled={googleLoading}
                      >
                        <Plus size={16} />
                        Create New Sheet
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="spreadsheet-configured">
                    <div className="sheet-info">
                      <FileSpreadsheet size={18} />
                      <span>Active Sheet: <strong>{googleStatus.spreadsheet_name}</strong></span>
                      <a 
                        href={`https://docs.google.com/spreadsheets/d/${googleStatus.spreadsheet_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-sheet-link"
                      >
                        <ExternalLink size={14} />
                        View
                      </a>
                    </div>
                    
                    <div className="sync-actions">
                      <Button 
                        onClick={syncExistingData} 
                        className="sync-btn"
                        disabled={googleLoading}
                      >
                        {googleLoading ? (
                          <>
                            <RefreshCw size={16} className="animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <RefreshCw size={16} />
                            Sync Existing Data
                          </>
                        )}
                      </Button>
                      <Button 
                        onClick={fetchSpreadsheets} 
                        className="change-sheet-btn"
                        disabled={googleLoading}
                      >
                        Change Sheet
                      </Button>
                    </div>
                  </div>
                )}
                
                <Button 
                  onClick={disconnectGoogle} 
                  className="disconnect-btn"
                  disabled={googleLoading}
                >
                  <Unlink size={16} />
                  Disconnect Google
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Spreadsheet Selection Modal */}
      {showSpreadsheetModal && (
        <div className="modal-overlay" onClick={() => setShowSpreadsheetModal(false)}>
          <div className="spreadsheet-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Select a Spreadsheet</h3>
            
            <div className="create-new-sheet">
              <h4>Create New Spreadsheet</h4>
              <div className="create-sheet-form">
                <input
                  type="text"
                  value={newSheetName}
                  onChange={(e) => setNewSheetName(e.target.value)}
                  placeholder="Spreadsheet name"
                  className="sheet-name-input"
                />
                <Button onClick={createNewSpreadsheet} disabled={googleLoading}>
                  {googleLoading ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
            
            <div className="existing-sheets">
              <h4>Or Select Existing</h4>
              {spreadsheets.length === 0 ? (
                <p className="no-sheets">No spreadsheets found in your Google Drive.</p>
              ) : (
                <div className="sheets-list">
                  {spreadsheets.map((sheet) => (
                    <button
                      key={sheet.id}
                      className="sheet-item"
                      onClick={() => selectSpreadsheet(sheet.id, sheet.name)}
                      disabled={googleLoading}
                    >
                      <FileSpreadsheet size={16} />
                      {sheet.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Button onClick={() => setShowSpreadsheetModal(false)} className="cancel-btn">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleSettingsTab;
